import * as React from 'react';
import clsx from 'clsx';
import {
  makeStyles,
  createStyles,
  CircularProgress,
  Fade,
  BbThemeProvider,
  getColor,
} from '@bb-ui/react-library';
import { useTranslation } from 'react-i18next';

interface LoadingIndicatorProps {
  isLoading?: boolean;
  theme?: 'light' | 'dark';
  variant?: 'fill-window' | 'fill-content';
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

export const useStyles = makeStyles(() =>
  createStyles({
    root: {
      width: '100%',
      height: '100%',
      display: 'grid',
      justifyContent: 'center',
      alignContent: 'center',
    },
    fillWindow: (props: LoadingIndicatorProps) => ({
      position: 'fixed' as 'fixed',
      zIndex: 99999,
      backgroundColor: getColor('background.default', props.theme || 'light'),
    }),
    loadingIndicator: (props: LoadingIndicatorProps) => ({
      color: getColor('brand.main', props.theme || 'light'),
    }),
  }),
);

const LoadingContent = React.forwardRef<
  HTMLDivElement,
  LoadingIndicatorProps & { classes: any; t: any }
>(({ classes, t, theme, variant, size, className: consumerClassName, ...other }, ref) => (
  <BbThemeProvider theme={theme || 'light'}>
    <div
      ref={ref}
      className={clsx(
        classes.root,
        {
          [classes.fillWindow]: variant === 'fill-window',
        },
        consumerClassName,
      )}
      data-testid="loading-indicator"
      {...other}
    >
      <CircularProgress
        ariaLabel={t('global.loadingIndicator.loading').toString()}
        size={size}
        className={classes.loadingIndicator}
      />
    </div>
  </BbThemeProvider>
));

LoadingContent.displayName = 'LoadingContent';

export const LoadingIndicator: React.FunctionComponent<LoadingIndicatorProps> = (props) => {
  const classes = useStyles(props);
  const { t } = useTranslation();
  const { isLoading, ...other } = props;

  return (
    <Fade mountOnEnter unmountOnExit in={isLoading} timeout={{ enter: 0, exit: 200 }}>
      <LoadingContent classes={classes} t={t} {...other} />
    </Fade>
  );
};

LoadingIndicator.defaultProps = {
  isLoading: true,
};
